import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Image, AlertCircle, Tag, GripVertical, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

export default function Promocoes() {
  const [establishment, setEstablishment] = useState(null);
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    product_id: '',
    title: '',
    is_active: true,
    order: 0
  });

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('fetch_error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBanners = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/banners`);
      setBanners(response.data);
    } catch (err) {
      console.error('Error fetching banners:', err);
    }
  }, [establishment]);

  const fetchProducts = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/products`);
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, [establishment]);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchBanners();
      fetchProducts();
    }
  }, [establishment, fetchBanners, fetchProducts]);

  const handleOpenDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        image_url: banner.image_url,
        product_id: banner.product_id,
        title: banner.title || '',
        is_active: banner.is_active,
        order: banner.order
      });
    } else {
      setEditingBanner(null);
      setFormData({
        image_url: '',
        product_id: '',
        title: '',
        is_active: true,
        order: banners.length
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error('Informe a URL da imagem do banner');
      return;
    }
    if (!formData.product_id) {
      toast.error('Selecione o produto relacionado');
      return;
    }

    try {
      const data = {
        ...formData,
        order: parseInt(formData.order) || 0
      };

      if (editingBanner) {
        await axios.put(`${API}/establishments/${establishment.id}/banners/${editingBanner.id}`, data);
        toast.success('Banner atualizado!');
      } else {
        await axios.post(`${API}/establishments/${establishment.id}/banners`, data);
        toast.success('Banner criado!');
      }
      setShowDialog(false);
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Tem certeza que deseja excluir este banner?')) return;
    
    try {
      await axios.delete(`${API}/establishments/${establishment.id}/banners/${bannerId}`);
      toast.success('Banner removido!');
      fetchBanners();
    } catch (err) {
      toast.error('Erro ao remover banner');
    }
  };

  const toggleActive = async (banner) => {
    try {
      await axios.put(`${API}/establishments/${establishment.id}/banners/${banner.id}`, {
        is_active: !banner.is_active
      });
      toast.success(banner.is_active ? 'Banner desativado' : 'Banner ativado');
      fetchBanners();
    } catch (err) {
      toast.error('Erro ao alterar status');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Produto não encontrado';
  };

  const getProductPrice = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return null;
    if (product.is_on_sale && product.sale_price) {
      return { original: product.price, sale: product.sale_price };
    }
    return { original: product.price };
  };

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  const activeBanners = banners.filter(b => b.is_active).length;

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Tag className="w-6 h-6 text-red-500" />
            Promoções e Banners
          </h1>
          <p className="text-slate-500">
            Gerencie os banners promocionais da sua loja ({activeBanners}/10 ativos)
          </p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="bg-red-500 hover:bg-red-600"
          disabled={activeBanners >= 10}
          data-testid="add-banner-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Banner
        </Button>
      </div>

      {banners.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhum banner cadastrado</h3>
            <p className="text-slate-500 mb-4">
              Crie banners promocionais para destacar produtos na página inicial da sua loja
            </p>
            <Button onClick={() => handleOpenDialog()} className="bg-red-500 hover:bg-red-600">
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Banner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {banners.map((banner, index) => {
            const productPrice = getProductPrice(banner.product_id);
            return (
              <Card 
                key={banner.id} 
                className={`overflow-hidden ${!banner.is_active ? 'opacity-60' : ''}`}
                data-testid={`banner-card-${banner.id}`}
              >
                <div className="flex items-stretch">
                  {/* Banner Image Preview */}
                  <div className="w-48 h-32 bg-slate-100 flex-shrink-0 relative">
                    {banner.image_url ? (
                      <img 
                        src={banner.image_url} 
                        alt={banner.title || 'Banner'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Image className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      #{index + 1}
                    </div>
                  </div>
                  
                  {/* Banner Info */}
                  <CardContent className="flex-1 p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800">
                        {banner.title || 'Sem título'}
                      </h3>
                      <p className="text-sm text-slate-500">
                        Produto: <span className="text-slate-700">{getProductName(banner.product_id)}</span>
                      </p>
                      {productPrice && (
                        <p className="text-sm mt-1">
                          {productPrice.sale ? (
                            <>
                              <span className="line-through text-slate-400">{formatCurrency(productPrice.original)}</span>
                              <span className="ml-2 font-bold text-red-500">{formatCurrency(productPrice.sale)}</span>
                            </>
                          ) : (
                            <span className="text-green-600 font-medium">{formatCurrency(productPrice.original)}</span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          banner.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {banner.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => toggleActive(banner)}
                        title={banner.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {banner.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleOpenDialog(banner)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(banner.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Banner Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? 'Editar Banner' : 'Novo Banner Promocional'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="banner_image">URL da Imagem do Banner *</Label>
                <Input
                  id="banner_image"
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://..."
                  data-testid="banner-image-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tamanho recomendado: 800x400px
                </p>
              </div>

              {formData.image_url && (
                <div className="border rounded-lg overflow-hidden">
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-full h-40 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="banner_product">Produto Relacionado *</Label>
                <Select 
                  value={formData.product_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, product_id: value }))}
                >
                  <SelectTrigger data-testid="banner-product-select">
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.is_on_sale ? product.sale_price : product.price)}
                        {product.is_on_sale && ' (PROMO)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Ao clicar no banner, o cliente verá este produto
                </p>
              </div>

              <div>
                <Label htmlFor="banner_title">Título (opcional)</Label>
                <Input
                  id="banner_title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ex: Super Promoção de Verão"
                  data-testid="banner-title-input"
                />
              </div>

              <div>
                <Label htmlFor="banner_order">Ordem de Exibição</Label>
                <Input
                  id="banner_order"
                  type="number"
                  min="0"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                  data-testid="banner-order-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600">
                {editingBanner ? 'Salvar' : 'Criar Banner'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
